<template>
  <v-dialog
    v-model="alwaysOpen"
    :fullscreen="isMobile"
    width="80%"
    max-width="512px"
    @click:outside="close"
    @keydown.esc="close"
  >
    <v-card class="form-dialog form-dialog--new-user">
      <v-icon
        class="form-dialog__button form-dialog__button--close"
        @click="close"
      >
        mdi-close
      </v-icon>

      <h1 class="form-dialog__title">
        Vehicle based discount{{ isListLocationProductLevel ? '' : ' - default' }}
      </h1>
      <span class="form-dialog__subtitle">
        {{ subtitle() }}
      </span>
      <v-form ref="form" class="mt-8">
        <v-row dense>
          <v-col cols="12" style="height: 64px">
            <div class="text-input-label">List name</div>
            <v-text-field
              solo
              flat
              dense
              background-color="#F6F6F6"
              v-model="formData.discount_list_name"
              class="pkmn-input"
              placeholder="Enter list name"
              :disabled="isLocationProduct || mode === 'edit'"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6" style="height: 64px">
            <div class="text-input-label">Valid from</div>
            <v-menu
              v-model="validFromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
              :disabled="isLocationProduct || mode === 'edit'"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatDate(formData.valid_from)"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  solo
                  flat
                  dense
                  background-color="#F6F6F6"
                  class="pkmn-input"
                  placeholder="dd.mm.yyyy"
                  append-icon="mdi-calendar"
                  :disabled="isLocationProduct || mode === 'edit'"
                />
              </template>
              <v-date-picker
                :value="toIsoDate(formData.valid_from)"
                @input="onValidFromChange"
                no-title
                color="primary"
                :first-day-of-week="1"
              />
            </v-menu>
          </v-col>
          <v-col cols="6" style="height: 64px">
            <div class="text-input-label">Valid until</div>
            <v-menu
              v-model="validToMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
              :disabled="isLocationProduct && !isListLocationProductLevel"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatDate(formData.valid_to)"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  solo
                  flat
                  dense
                  background-color="#F6F6F6"
                  class="pkmn-input"
                  placeholder="dd.mm.yyyy"
                  append-icon="mdi-calendar"
                  :disabled="isLocationProduct && !isListLocationProductLevel"
                />
              </template>
              <v-date-picker
                :value="toIsoDate(formData.valid_to)"
                @input="onValidToChange"
                no-title
                color="primary"
                :first-day-of-week="1"
              />
            </v-menu>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6" style="height: 64px">
            <div class="text-input-label">Apply to product</div>
            <v-select
              solo
              flat
              dense
              disabled
              background-color="#F6F6F6"
              :items="['Visitor parking']"
              value="Visitor parking"
              class="pkmn-input"
            />
          </v-col>
          <v-col cols="6" style="height: 64px">
            <div class="text-input-label">Discount</div>
            <v-text-field
              solo
              flat
              dense
              background-color="#F6F6F6"
              v-model="formData.discount_percentage"
              class="pkmn-input"
              suffix="%"
              type="number"
              :min="0"
              :max="100"
              :disabled="isLocationProduct || mode === 'edit'"
            />
          </v-col>
        </v-row>
        <v-row v-if="showVehicleOps">
          <v-col cols="12" style="height: 44px"></v-col>
        </v-row>
        <v-row dense v-if="showVehicleOps">
          <v-col cols="12" style="height: 64px">
            <div class="text-input-label">Add / Remove / Search vehicle</div>
            <v-text-field
              solo
              flat
              dense
              background-color="#F6F6F6"
              v-model="carNumber"
              :hint="ecoCarHint"
              :error="error !== null"
              :error-messages="error"
              persistent-hint
              class="pkmn-input"
            />
          </v-col>
        </v-row>

        <v-row class="form-dialog__button-wrap" v-if="showVehicleOps">
          <v-col cols="4">
            <v-btn
              class="pkmn-button"
              outlined
              color="primary"
              :disabled="!canUseVehicleOps || !carNumber"
              @click="search"
            >
              Search
            </v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn
              class="pkmn-button"
              outlined
              color="primary"
              :disabled="!canUseVehicleOps || !carNumber"
              @click="remove"
            >
              Remove
            </v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn
              class="pkmn-button pkmn-button--confirm pkmn-button--has-border"
              color="primary"
              :disabled="!canAddVehicle"
              @click="add"
            >
              Add
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="form-dialog__button-wrap" v-if="showVehicleOps">
          <v-col cols="12">
            <v-btn
              class="pkmn-button pkmn-button--confirm pkmn-button--has-border"
              block
              color="primary"
              :disabled="!canSave"
              @click="save"
            >
              Save
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import merchantHelpers from '@/helpers/merchant'

export default {
  name: 'FormEcoCarDiscount',
  props: {
    discount: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'create',
      validator: (val) => ['create', 'edit'].includes(val)
    },
    isLocationProduct: {
      type: Boolean,
      default: false
    },
    // Location product ids of the list being edited; required for vehicle
    // ops and valid_to updates on location_product level lists.
    locationProductIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      alwaysOpen: true,
      mobileBreakpoint: 767,
      validFromMenu: false,
      validToMenu: false,
      carNumber: null,
      ecoCarHint: '',
      error: null,
      formData: {
        discount_list_name: '',
        valid_from: null,
        valid_to: null,
        discount_percentage: null
      },
      listId: null,
      changed: false
    }
  },
  computed: {
    isMobile() {
      return window.innerWidth <= this.mobileBreakpoint
    },
    isListLocationProductLevel() {
      if (!this.discount) return false
      return (
        (this.discount.discount_level || this.discount.type) ===
        'location_product'
      )
    },
    // Vehicle ops (and Save) show on the merchant page for any list, and on
    // the location page only for the location's own (override) list — the
    // account-default list stays read-only there.
    showVehicleOps() {
      return !this.isLocationProduct || this.isListLocationProductLevel
    },
    isMetadataValid() {
      if (!this.formData.discount_list_name) return false
      if (!this.formData.valid_from) return false
      const pct = this.formData.discount_percentage
      if (pct === null || pct === '' || pct < 0 || pct > 100) return false
      return true
    },
    canUseVehicleOps() {
      return !!this.listId
    },
    canAddVehicle() {
      if (!this.carNumber) return false
      if (this.listId) return true
      return this.isMetadataValid
    },
    canSave() {
      if (this.mode === 'edit') {
        return this.formData.valid_to !== (this.discount?.valid_to || null)
      }
      return this.isMetadataValid
    }
  },
  methods: {
    close() {
      this.$emit('close', this.changed)
    },
    formatDate(timestamp) {
      if (!timestamp) return ''
      return moment.unix(timestamp).format('DD.MM.YYYY')
    },
    toIsoDate(timestamp) {
      if (!timestamp) return null
      return moment.unix(timestamp).format('YYYY-MM-DD')
    },
    onValidFromChange(isoDate) {
      this.formData.valid_from = moment(isoDate, 'YYYY-MM-DD')
        .startOf('day')
        .unix()
      this.validFromMenu = false
    },
    onValidToChange(isoDate) {
      this.formData.valid_to = moment(isoDate, 'YYYY-MM-DD')
        .startOf('day')
        .unix()
      this.validToMenu = false
    },
    async ensureList() {
      if (this.listId) return this.listId
      if (!this.isMetadataValid) return null

      const merchantId = this.$route.params.merchantId
      // Backend rejects valid_from older than (server now − 3s). When the
      // user picks today, recompute at request time to land inside the
      // tolerance window.
      const now = moment().unix()
      const payload = {
        discount_list_name: this.formData.discount_list_name,
        valid_from:
          this.formData.valid_from && this.formData.valid_from < now
            ? now
            : this.formData.valid_from,
        valid_to: this.formData.valid_to,
        discount_percentage: Number(this.formData.discount_percentage)
      }
      const created = await merchantHelpers.createMerchantEcoCarsList(
        merchantId,
        payload
      )
      if (!created) return null

      let newListId = created.discount_list_id || created.list_id
      if (!newListId) {
        // Create response may come back without a list id; refetch and match
        // by name + level (same workaround as holiday lists).
        const allLists = await merchantHelpers.fetchEcoCarsDiscountLists(
          merchantId
        )
        const candidates = (allLists || []).filter(
          (d) =>
            d.discount_list_name === payload.discount_list_name &&
            (d.discount_level || d.type) === 'merchant'
        )
        candidates.sort(
          (a, b) => (b.discount_list_id || 0) - (a.discount_list_id || 0)
        )
        newListId = candidates[0]?.discount_list_id
      }
      if (newListId) {
        this.listId = newListId
        this.changed = true
        return this.listId
      }
      return null
    },
    // Resolve the entered plate to its mapping on the current list.
    // The backend returns a single mapping object (or 404 -> null); tolerate
    // an array shape too in case the endpoint ever returns a collection.
    async findMapping() {
      const merchantId = this.$route.params.merchantId
      let result
      if (this.isListLocationProductLevel) {
        const lpId = this.locationProductIds[0]
        if (!lpId) return null
        result = await merchantHelpers.findEcoCarInLocationProductList(
          merchantId,
          lpId,
          this.listId,
          this.carNumber
        )
      } else {
        result = await merchantHelpers.findEcoCarInMerchantList(
          merchantId,
          this.listId,
          this.carNumber
        )
      }
      if (!result) return null
      const mapping = Array.isArray(result) ? result[0] : result
      // The delete endpoint expects eco_car_id (the spec mislabels its path
      // param as eco_car_mapping_id), so accept the mapping when either id is
      // present and let remove() pick eco_car_id.
      return mapping && (mapping.eco_car_id || mapping.eco_car_mapping_id)
        ? mapping
        : null
    },
    async add() {
      this.error = null
      this.ecoCarHint = ''
      const listId = await this.ensureList()
      if (!listId) {
        this.ecoCarHint = 'Something went wrong, try again'
        return
      }
      const merchantId = this.$route.params.merchantId
      let result
      if (this.isListLocationProductLevel) {
        const lpId = this.locationProductIds[0]
        result = lpId
          ? await merchantHelpers.addEcoCarToLocationProductList(
              merchantId,
              lpId,
              listId,
              this.carNumber
            )
          : null
      } else {
        result = await merchantHelpers.addEcoCarToMerchantList(
          merchantId,
          listId,
          this.carNumber
        )
      }
      if (result) {
        this.ecoCarHint = 'Vehicle is added'
        this.changed = true
      } else {
        this.ecoCarHint = 'Something went wrong, try again'
      }
    },
    async remove() {
      this.error = null
      this.ecoCarHint = ''
      // Delete is keyed by mapping id (the {listId}/cars collection path is
      // GET-only — DELETE there returns 405), so resolve the plate to its
      // mapping via the find endpoint first, then delete by that id.
      const mapping = await this.findMapping()
      if (!mapping) {
        this.error = 'Vehicle is not on the list'
        this.ecoCarHint = 'Vehicle is not on the list'
        return
      }
      // Backend deletes by eco_car_id despite the spec naming the path param
      // eco_car_mapping_id; fall back to mapping id only if eco_car_id is absent.
      const idToDelete = mapping.eco_car_id ?? mapping.eco_car_mapping_id
      // location_product lists delete via their own path; merchant lists via
      // the product-scoped one. Pass lpId only for the former.
      const lpId = this.isListLocationProductLevel
        ? this.locationProductIds[0]
        : null
      const result = await merchantHelpers.deleteEcoCarFromList(
        this.$route.params.merchantId,
        this.listId,
        idToDelete,
        lpId
      )
      if (result) {
        this.ecoCarHint = 'Vehicle is removed'
        this.changed = true
      } else {
        this.ecoCarHint = 'Something went wrong, try again'
      }
    },
    async search() {
      this.error = null
      this.ecoCarHint = ''
      const mapping = await this.findMapping()
      if (mapping) {
        this.error = null
        this.ecoCarHint = 'Vehicle is on the list'
      } else {
        this.error = 'Vehicle is not on the list'
        this.ecoCarHint = 'Vehicle is not on the list'
      }
    },
    async save() {
      if (this.mode === 'edit') {
        const merchantId = this.$route.params.merchantId
        let result
        if (this.isListLocationProductLevel) {
          const lpId = this.locationProductIds[0]
          result = lpId
            ? await merchantHelpers.updateLocationProductEcoCarsList(
                merchantId,
                lpId,
                this.listId,
                { valid_to: this.formData.valid_to }
              )
            : false
        } else {
          result = await merchantHelpers.updateMerchantEcoCarsList(
            merchantId,
            this.listId,
            { valid_to: this.formData.valid_to }
          )
        }
        if (!result) return
        this.changed = true
        this.close()
        return
      }
      const listId = await this.ensureList()
      if (listId) {
        this.close()
      }
    },
    subtitle() {
      if (this.isListLocationProductLevel) {
        return 'This discount applies to this location only. You can manage its vehicles and validity here.'
      }
      if (this.isLocationProduct) {
        return 'This is your account default discount. All locations will apply the discount. To change the discount settings for this location only, please contact parkman customer support'
      }
      return 'This is your account default discount. All locations will apply the discount.'
    }
  },
  created() {
    // Populate from the selected list when editing, and also for the
    // read-only view on the location page (which doesn't pass mode="edit").
    if (this.discount) {
      this.formData.discount_list_name =
        this.discount.discount_list_name || ''
      this.formData.valid_from = this.discount.valid_from || null
      this.formData.valid_to = this.discount.valid_to || null
      this.formData.discount_percentage =
        this.discount.discount_percentage || null
      this.listId =
        this.discount.list_id || this.discount.discount_list_id || null
    }
  }
}
</script>

<style lang="scss">
@import '@/style/common';

.v-text-field__details {
  padding: 0px !important;
}

.col:has(.v-input--is-focused) {
  .text-input-label {
    color: $color-brand;
  }
}

.form-dialog--new-user .v-input--is-focused .v-input__slot {
  border: 1px solid $color-brand !important;
}

.form-dialog--new-user .v-input.error--text .v-input__slot {
  border: 1px solid $color-danger !important;
}

.my-text-field ::v-deep .v-text-field__details .v-messages {
  display: flex;
  justify-content: flex-end;
}
</style>
